import { createFileRoute, Link } from "@tanstack/react-router";
import portraitAsset from "@/assets/a-propos.webp.asset.json";

export const Route = createFileRoute("/energeticienne-fonsorbes")({
  head: () => ({
    meta: [
      { title: "Énergéticienne à Fonsorbes — Maison Hyggee | Rénergescence" },
      { name: "description", content: "Caroline Balech, énergéticienne à Fonsorbes (Maison Hyggee). Soins énergétiques, magnétisme et naturopathie à l'ouest toulousain. Prenez rendez-vous." },
      { property: "og:title", content: "Énergéticienne à Fonsorbes — Maison Hyggee | Rénergescence" },
      { property: "og:description", content: "Soins énergétiques, magnétisme et approche holistique à Fonsorbes, à la Maison Hyggee." },
      { property: "og:url", content: "/energeticienne-fonsorbes" },
      { property: "og:image", content: portraitAsset.url },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/energeticienne-fonsorbes" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "LocalBusiness",
              "@id": "https://renergescence.com/#localbusiness-fonsorbes",
              name: "Rénergescence — Maison Hyggee Fonsorbes",
              image: portraitAsset.url,
              url: "https://renergescence.com/energeticienne-fonsorbes",
              telephone: "+33689676546",
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2643 route de Tarbes",
                addressLocality: "Fonsorbes",
                postalCode: "31470",
                addressCountry: "FR",
              },
              areaServed: [
                { "@type": "City", name: "Fonsorbes" },
                { "@type": "City", name: "Plaisance-du-Touch" },
                { "@type": "City", name: "Toulouse" },
              ],
            },
            {
              "@type": "Person",
              name: "Caroline Balech",
              jobTitle: "Énergéticienne, praticienne en soins énergétiques et naturopathe",
              worksFor: { "@id": "https://renergescence.com/#localbusiness-fonsorbes" },
              image: portraitAsset.url,
              areaServed: { "@type": "City", name: "Fonsorbes" },
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const soins = [
  { name: "Soin énergétique global", desc: "Rééquilibrage profond des centres énergétiques." },
  { name: "Magnétisme", desc: "Apaisement par imposition des mains, en cabinet ou à distance." },
  { name: "Massage énergétique", desc: "Travail manuel et énergétique combiné." },
  { name: "Naturopathie", desc: "Bilan et programme d'hygiène de vie personnalisé." },
];

const faqs = [
  { q: "Où se trouve le cabinet à Fonsorbes ?", a: "Les séances se tiennent à la Maison Hyggee, 2643 route de Tarbes, 31470 Fonsorbes — un espace dédié au bien-être, accessible depuis Plaisance-du-Touch, Saint-Lys, La Salvetat-Saint-Gilles et l'ouest toulousain." },
  { q: "Quels soins sont proposés à la Maison Hyggee ?", a: "Soins énergétiques, magnétisme, massages énergétiques et accompagnements en santé naturelle (naturopathie). Chaque séance est adaptée à votre demande et à votre état du moment." },
  { q: "Combien de temps dure une séance ?", a: "Comptez environ 1h à 1h30 pour une première séance, échange et soin compris." },
  { q: "Faut-il croire pour que cela fonctionne ?", a: "Non. L'énergétique agit indépendamment des croyances. Une posture d'ouverture suffit pour accueillir les effets de la séance." },
];

const testimonials = [
  { n: "Amélie L. — Avis Google", t: "Une séance extraordinaire à la Maison Hyggee de Fonsorbes. Caroline est d'une bienveillance et d'une écoute remarquables. Le soin énergétique m'a procuré un profond sentiment de libération et de paix. Je reviendrai sans hésiter." },
  { n: "Thomas B. — Avis Google", t: "Praticienne très professionnelle et attentive. J'ai consulté pour un blocage persistant et des tensions physiques. Le soin énergétique a été extrêmement efficace et ressourçant. Le lieu à Fonsorbes est également très agréable." },
  { n: "Sandrine M. — Avis Google", t: "Caroline propose un accompagnement global d'une grande justesse. On se sent tout de suite en confiance. Son approche m'a aidée à retrouver toute mon énergie et à mieux gérer mon stress. Je la recommande chaleureusement !" },
];

function Page() {
  return (
    <>
      <section className="pt-20 pb-12 md:pt-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="eyebrow">Fonsorbes · Maison Hyggee · À distance</p>
          <h1 className="mt-5 font-display text-4xl font-light italic md:text-6xl">Énergéticienne à Fonsorbes</h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed md:text-lg" style={{ color: "var(--color-muted-foreground)" }}>
            Caroline Balech vous accueille à la Maison Hyggee de Fonsorbes pour des soins énergétiques, du magnétisme et un accompagnement en santé naturelle — au plus près de l'ouest toulousain.
          </p>
          <Link to="/contact" className="mt-8 inline-block rounded-full px-8 py-4 text-sm font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>
            Prendre rendez-vous
          </Link>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="mx-auto grid max-w-[1100px] gap-12 px-6 md:grid-cols-[360px_1fr] md:items-center">
          <div className="overflow-hidden rounded-3xl">
            <img src={portraitAsset.url} alt="Caroline Balech, énergéticienne à Fonsorbes" width={720} height={900} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <div>
            <h2 className="font-display text-3xl font-light md:text-4xl">Une approche globale, au cœur de Fonsorbes</h2>
            <p className="mt-5 text-base leading-relaxed" style={{ color: "var(--color-text)" }}>
              Guidée par vos énergies et à l'écoute de mes ressentis intérieurs, j'accompagne chaque personne dans sa singularité. Mes connaissances en santé naturelle acquises à l'E.I.B.E de Toulouse me permettent d'avoir une vision globale et pluridimensionnelle de chacun. La séance vise à libérer les blocages énergétiques, soutenir le terrain et restaurer l'équilibre intérieur.
            </p>
            <ul className="mt-6 space-y-3 text-[15px]" style={{ color: "var(--color-text)" }}>
              <li>• Stress, anxiété, charge mentale</li>
              <li>• Fatigue chronique, manque de vitalité</li>
              <li>• Douleurs et tensions corporelles</li>
              <li>• Transitions de vie, accompagnement émotionnel</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/prestations" hash="soins-energetiques" className="rounded-full border px-5 py-2 text-sm" style={{ borderColor: "#7A8A76", color: "var(--color-sage-text)" }}>Voir les soins</Link>
              <Link to="/contact" className="rounded-full px-5 py-2 text-sm text-white" style={{ backgroundColor: "#7A8A76" }}>Prendre rendez-vous</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Les soins proposés à Fonsorbes</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {soins.map((s) => (
              <div key={s.name} className="rounded-3xl border bg-white p-6" style={{ borderColor: "#e8e0d8" }}>
                <h3 className="font-display text-lg" style={{ color: "#2D2B28" }}>{s.name}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--color-text)" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="mx-auto grid max-w-[1100px] gap-10 px-6 md:grid-cols-2 md:items-center">
          <div>
            <h2 className="font-display text-3xl font-light md:text-4xl">La Maison Hyggee, à Fonsorbes</h2>
            <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>
              La Maison Hyggee est un lieu pensé pour le bien-être, idéalement situé à Fonsorbes pour accueillir les patients de l'ouest toulousain — Plaisance-du-Touch, Saint-Lys, La Salvetat-Saint-Gilles, Léguevin.
            </p>
            <address className="mt-6 not-italic text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>
              <strong>Maison Hyggee</strong><br />
              2643 route de Tarbes<br />
              31470 Fonsorbes
            </address>
            <p className="mt-4 text-[14px]" style={{ color: "var(--color-muted-foreground)" }}>
              Téléphone : <a href="tel:+33689676546" className="underline">06 89 67 65 46</a>
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border" style={{ borderColor: "#e8e0d8" }}>
            <iframe
              title="Plan d'accès à la Maison Hyggee à Fonsorbes"
              src="https://www.google.com/maps?q=2643+Route+de+Tarbes+31470+Fonsorbes&output=embed"
              width="100%"
              height="320"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Ils témoignent</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.n} className="rounded-3xl border bg-white p-6" style={{ borderColor: "#e8e0d8" }}>
                <blockquote className="font-display text-lg italic leading-snug" style={{ color: "#2D2B28" }}>« {t.t} »</blockquote>
                <figcaption className="mt-4 text-xs uppercase tracking-[0.18em]" style={{ color: "#6a6258" }}>{t.n}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ backgroundColor: "#F8F5F0" }}>
        <div className="mx-auto max-w-[820px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Questions fréquentes</h2>
          <dl className="mt-10 space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e8e0d8" }}>
                <dt className="font-display text-lg" style={{ color: "#2D2B28" }}>{f.q}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>{f.a}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-10 text-center">
            <Link to="/contact" className="inline-block rounded-full px-8 py-4 text-sm font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>
              Réserver ma séance
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}